import React, { ReactElement, useCallback, useEffect } from "react";
import {
    closestCenter,
    DragEndEvent,
    DragStartEvent,
    UniqueIdentifier,
} from "@dnd-kit/core";
import {
    SortableContext,
    SortingStrategy,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortableDragEventHandlers } from "organisms/sortable-drap-and-drop/use-sortable-drag-event-handlers";
import {
    restrictToFirstScrollableAncestor,
    restrictToParentElement,
    snapCenterToCursor,
} from "@dnd-kit/modifiers";
import { useRegisterDragAndDropScopeContext } from "utilities/contexts/shared-drag-and-drop/register-drag-and-drop-scope-context";
import { DragAndDropScopeConfiguration } from "utilities/contexts/shared-drag-and-drop/drag-and-drop-scope-configuration";

export type SortableDragAndDropScopeConfiguration = Pick<
    DragAndDropScopeConfiguration,
    | "collisionDetection"
    | "contextModifiers"
    | "draggableComponent"
    | "overlayModifiers"
>;

export interface SharedSortableDragAndDropListProps {
    children: ReactElement<{ id: UniqueIdentifier }>[];
    dragAndDropScopeConfiguration?: SortableDragAndDropScopeConfiguration;
    dragAndDropScopeName?: string;
    items: { id?: any }[];
    onDragEnd?: () => void;
    onDragStart?: (event: DragStartEvent) => void;
    onSortChange: (any) => void;
    sortProperty?: string;
    strategy?: SortingStrategy;
}

const DEFAULT_DISPLAY_ORDER_PROPERTY = "displaySequence";
const DEFAULT_ON_DRAG_START = () => {};
export const DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME = "sortable";
export const DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_CONFIGURATION: SortableDragAndDropScopeConfiguration =
    {
        collisionDetection: closestCenter,
        contextModifiers: [
            restrictToParentElement,
            restrictToFirstScrollableAncestor,
        ],
        overlayModifiers: [snapCenterToCursor],
    };

export const SharedSortableDragAndDropList = (
    props: SharedSortableDragAndDropListProps
) => {
    const {
        children,
        dragAndDropScopeConfiguration,
        dragAndDropScopeName = DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME,
        items,
        onDragEnd,
        onDragStart = DEFAULT_ON_DRAG_START,
        onSortChange,
        sortProperty = DEFAULT_DISPLAY_ORDER_PROPERTY,
        strategy = verticalListSortingStrategy,
    } = props;

    const { handleDragEnd } = useSortableDragEventHandlers(
        items,
        onSortChange,
        sortProperty
    );

    const customHandleDragEnd = useCallback(
        (event: DragEndEvent) => {
            handleDragEnd(event);
            onDragEnd?.();
        },
        [handleDragEnd, onDragEnd]
    );

    const { registerDragAndDropScope } = useRegisterDragAndDropScopeContext();

    useEffect(
        () =>
            registerDragAndDropScope(dragAndDropScopeName, {
                ...DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_CONFIGURATION,
                ...(dragAndDropScopeConfiguration ?? {}),
                handleDragEnd: customHandleDragEnd,
                handleDragStart: onDragStart,
            }),
        [
            dragAndDropScopeConfiguration,
            dragAndDropScopeName,
            customHandleDragEnd,
            onDragStart,
            registerDragAndDropScope,
        ]
    );

    return (
        <SortableContext
            items={items as unknown as { id: UniqueIdentifier }[]}
            strategy={strategy}>
            {children}
        </SortableContext>
    );
};
